import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_research-precision-1/artifacts/0d2556841c9f64b9_logo%20lighter.jpeg";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/c2a0fed7-aaf7-4cad-a28f-ec61cfaefdc6/images/ab0771eb5251978cf642b728111ce3ba3432462e173cf785710adb6c0badc1de.jpeg";

export const Hero = () => {
  const navigate = useNavigate();
  const tapRef = useRef({ count: 0, t: 0 });

  const secretTap = () => {
    const now = Date.now();
    const s = tapRef.current;
    s.count = now - s.t < 1500 ? s.count + 1 : 1;
    s.t = now;
    if (s.count >= 5) { s.count = 0; navigate("/control"); }
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Bright modern research laboratory" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950/50" />
      </div>

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center pt-32 pb-44 text-center sm:pt-36 sm:pb-52">
          <h1 className="sr-only">PepsWorld UK — Your. Final. Form.</h1>

          <motion.span
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-4 py-1.5 text-xs font-medium tracking-wide text-white"
          >
            <MapPin className="h-3.5 w-3.5 text-blue-300" /> Serving UK researchers, laboratories &amp; educators
          </motion.span>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.05 }}
            className="relative mt-8"
          >
            <div className="relative grid animate-float place-items-center">
              <div className="absolute -inset-6 rounded-full bg-blue-500/25 blur-3xl" aria-hidden="true" />
              {/* rotating blue ring */}
              <motion.div
                aria-hidden="true"
                className="absolute h-[16.5rem] w-[16.5rem] rounded-full sm:h-[20.5rem] sm:w-[20.5rem]"
                style={{ background: "conic-gradient(from 0deg, transparent 0deg, rgba(59,130,246,0.15) 60deg, #3B82F6 150deg, #60A5FA 210deg, rgba(59,130,246,0.15) 300deg, transparent 360deg)" }}
                animate={{ rotate: 360 }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
              />
              {/* orbiting dot */}
              <motion.div
                aria-hidden="true"
                className="absolute h-[16.5rem] w-[16.5rem] sm:h-[20.5rem] sm:w-[20.5rem]"
                animate={{ rotate: 360 }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
              >
                <span className="absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 rounded-full bg-blue-400 shadow-[0_0_14px_4px_rgba(96,165,250,0.8)]" />
              </motion.div>
              {/* logo circle (white cropped out) */}
              <div data-testid="hero-logo" onClick={secretTap} className="relative h-64 w-64 cursor-pointer overflow-hidden rounded-full shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] sm:h-80 sm:w-80 select-none">
                <img src={LOGO} alt="PepsWorld UK — Your. Final. Form." className="h-full w-full scale-[1.18] object-cover" />
              </div>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-200 sm:text-lg"
          >
            Premium research peptides. Built for precision. GHK-Cu, Retatrutide and MT-2 — supplied for lawful UK laboratory research.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <Button data-testid="hero-cta-explore" onClick={() => navigate("/shop")} className="btn-metallic h-12 px-7 text-base font-semibold">
              Explore Research <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button data-testid="hero-cta-learn" onClick={() => document.getElementById("transparency")?.scrollIntoView({ behavior: "smooth" })} variant="outline" className="h-12 px-7 text-base border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white">
              Learn More
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
